import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAuthApi } from '@/features/auth/api/adminAuthApi';
import { lockedUntilLabel, loginRefusal } from '@/features/auth/adminAuth';
import type { LoginRefusal } from '@/features/auth/types/LoginRefusal';
import { PasswordField } from '@/features/auth/PasswordField';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { adminAuth } from '@/i18n/dictionaries/adminAuth';
import { useTranslation } from '@/i18n/useTranslation';
import { ADMIN_PASSWORD_PATH } from '@/services/adminSession';
import { ApiError, problemDetail } from '@/services/api';
import { consumeReturnTo } from '@/services/reauth';
import '@/features/auth/admin-auth.css';

/**
 * /admin/autentificare — the administration's own sign-in for SYSTEM_ADMIN accounts (ADM-1), on
 * its host: outside every shell, as the MPass screens are, a centred card with the platform's mark.
 * The server's refusals are shown under the form in its words — a wrong name or password alike, a
 * credential locked after too many tries with when it opens again. Signed in, the tab goes on to
 * change a temporary password first, or back to the cabinet page it was sent away from.
 */
export function AdminLoginPage() {
  const { t, language } = useTranslation(adminAuth);
  useDocumentTitle(t('loginTitle'));
  const navigate = useNavigate();
  const usernameId = useId();
  const passwordId = useId();
  const usernameRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [missing, setMissing] = useState<{ username?: boolean; password?: boolean }>({});
  const [refusal, setRefusal] = useState<LoginRefusal | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const submit = async () => {
    const empty = { username: !username.trim(), password: !password };
    setMissing(empty);
    if (empty.username || empty.password) return;
    setBusy(true);
    setRefusal(null);
    try {
      const answer = await adminAuthApi.login(username.trim(), password);
      if (answer.mustChangePassword) {
        navigate(ADMIN_PASSWORD_PATH, { replace: true });
        return;
      }
      // A full navigation: the cabinet's session is read when the app boots.
      window.location.assign(consumeReturnTo() ?? '/app/cabinet');
    } catch (e) {
      const until =
        e instanceof ApiError ? (e.problem as { lockedUntil?: string }).lockedUntil : undefined;
      setRefusal(loginRefusal(problemDetail(e), lockedUntilLabel(until, language)));
      setPassword('');
      setBusy(false);
    }
  };

  return (
    <main className="admin-auth">
      <div className="admin-auth__wrap">
        <div className="admin-auth__card card">
          <div className="admin-auth__brand" aria-hidden="true">
            <span className="brand__mark">
              <span className="brand__mark-text">AK</span>
            </span>
            <span className="admin-auth__word">AKIS</span>
          </div>
          <h1>{t('loginTitle')}</h1>
          <p className="admin-auth__lead">{t('loginLead')}</p>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className="admin-auth__field">
              <label className="admin-auth__label" htmlFor={usernameId}>
                {t('username')}
              </label>
              <input
                ref={usernameRef}
                id={usernameId}
                className="text-input"
                autoComplete="username"
                spellCheck={false}
                value={username}
                aria-invalid={missing.username || undefined}
                onChange={(e) => setUsername(e.target.value)}
              />
              {missing.username && <p className="field-error">{t('usernameRequired')}</p>}
            </div>
            <PasswordField
              id={passwordId}
              label={t('password')}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              error={missing.password ? t('passwordRequired') : undefined}
            />
            {refusal && (
              <p className="message message--inline message--error" role="alert">
                {refusal.kind === 'server'
                  ? refusal.text
                  : refusal.kind === 'locked'
                    ? t('lockedUntil', { when: refusal.when })
                    : t('loginFailed')}
              </p>
            )}
            <button
              type="submit"
              className="btn btn--primary btn--md admin-auth__submit"
              disabled={busy}
            >
              {busy ? t('signingIn') : t('signIn')}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
